import { fieldValidation } from "../fieldValidation";
import { surveyValidation } from "./surveyFieldValidation";
import { basicValidation } from '../basicValidation';
import { ActionPlanTopicTypeData, ActionPlanTypeData, ActionPlanRiskConsequences, ActionPlanRiskLikelihoods, ActionPlanTypes } from "../../../DTOs";

export const actionPlanFieldValidation = {
    actionPlan: {
        id: (value: string) => basicValidation.resourceId.isValid(value),
        title: (value: string) => basicValidation.resourceText.isValid(value, false, 100),
        description: (value: string) => basicValidation.resourceText.isValid(value, true, 200),
        userIdOrNone: (value: string) => basicValidation.resourceId.isValidOrEmpty(value),
        type: (value: string) =>
        {
            return ActionPlanTypes.some(t => t === value);
        },
        typeData: (value: ActionPlanTypeData) =>
        {
            if( value === null || value === undefined || typeof value !== 'object' )
                return false;

            if (value.type === 'WPA')
            {
                return basicValidation.resourceId.isValid(value?.customData?.surveyId)
                    && value?.customData?.segmentFilter 
                    && surveyValidation.surveySegment.validateUniqueListOfLists(value.customData.segmentFilter);
            }
            else if (value.type === 'Independent')
            {
                return true; // No additional validation for independent action plans
            }
            else if(value.type === 'Article')
            {
                return basicValidation.resourceId.isValidOrEmpty(value.customData.articleId);
            }
            else if(value.type === 'Meeting')
            {
                return typeof value.customData?.hidden === 'boolean';
            }
            return false; // Invalid type
        }
    },

    actionPlanTopic: {
        id: (value: string) => basicValidation.resourceId.isValid(value),
        description: (value: string) => basicValidation.resourceText.isValid(value, false, 200),
        typeData: (value: ActionPlanTopicTypeData) =>
        {
            if (value === null || value === undefined || typeof value !== 'object')
            {
                return false; // Invalid typeData
            }

            if (value?.type === 'WPA')
            {
                if (!value?.customData?.surveyTopicIdentifier) return false;

                const identifier = value.customData.surveyTopicIdentifier;
                if (identifier === null) return false;
                if (identifier.linkType === 'theme')
                {
                    return fieldValidation.surveyTheme.index(identifier.themeIndex);
                }
                if (identifier.linkType === 'indicator')
                {
                    return fieldValidation.surveyQuestion.indicators([identifier.indicator]);
                }
                if (identifier.linkType === 'question')
                {
                    return fieldValidation.surveyTheme.index(identifier.themeIndex)
                        && fieldValidation.surveyQuestion.index(identifier.questionIndex);
                }
                return false;
            }

            if (value?.type === 'Basic')
            {
                return true; // No additional validation for Basic type
            }

            if (value?.type === 'AgendaItem')
            {
                return basicValidation.resourceId.isValid(value.customData.agendaItemId);
            }

            return false; // Invalid type
        },
        risk:(value: { consequence: string, likelihood: string }) =>
        {
            if(typeof value !== 'object' || value === null)
                return false;

            if(typeof value.consequence !== 'string') return false;
            if(typeof value.likelihood !== 'string') return false;
            
            return ActionPlanRiskConsequences.some(s => s === value.consequence)
                && ActionPlanRiskLikelihoods.some(s => s === value.likelihood);
        },

        userIdOrNone: (value: string) => basicValidation.resourceId.isValidOrEmpty(value),
    },

    actionPlanLink: {
        actionPlanId: (value: string) => basicValidation.resourceId.isValid(value),
        linkId: (value: string) => basicValidation.resourceId.isValid(value),
        configuration: (value: any) =>
        {
            if (!value || typeof value !== 'object') return false;

            if (value.type === 'actionPlan')
            {
                return typeof value.writeable === 'boolean';
            }
            else if (value.type === 'actionPlanTopic')
            {
                return basicValidation.resourceId.isValid(value.topicId) 
                    && typeof value.writeable === 'boolean';
            }
            else if (value.type === 'actionPoint')
            {
                return basicValidation.resourceId.isValid(value.actionPointId) 
                    && typeof value.writeable === 'boolean';
            }
            return false; // Invalid type
        },
        recipients: (values: string[]) => {
            return basicValidation.validateStringArray(values, 200)
                && basicValidation.arrayStringsAreUnique(values)
                && values.every(recipient => basicValidation.isValidEmail(recipient))
        },
        message: (value: string) => basicValidation.validateTextString(value, 0, 1000)
    }
};